import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { connect } from "react-redux";
import * as yup from "yup";
import { Formik } from "formik";
import { httpClient, vatBaseUrl, billingInformationBaseUrl } from "../../http-client/http-client";
import InputText from "../Forms/InputText";
import {
  makeStyles,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Tooltip,
  FormHelperText
} from "@material-ui/core";
import InputSelect from "../Forms/InputSelect";
import clsx from "clsx";
import { geoActions } from "../../store/actions/geo/geo";
import LogoSpinner from "../UI/LogoSpinner/LogoSpinner";
import CountryAutocompleteDS from "../Forms/CountryAutocompleteDS";
import Done from "../../icons/Done";
import WarningFull from "../../icons/WarningFull";
import useDebounce from "../../utils/useDebounce";
import { isEqual } from 'lodash';
import {logger} from '../../logging/logger';
import { useTranslation } from "react-i18next";

const log = logger('BillingInformationForm');

const useStyles = makeStyles(theme => ({
  formActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
  },
  submitError: {
    margin: 0,
  },
  textField: {
    // marginBottom: theme.spacing(1)
  },
  denseAdornment: {
    paddingBottom: "4px"
  },
  adornmentStart: {
    marginRight: "2px"
  },
  iconSuccess: {
    color: "green",
    fontSize: "16px",
  },
  iconWarning: {
    color: theme.palette.secondary.main,
    fontSize: "16px",
  }
}));

const customerTypeOptions = [
  { value: 0, name: "customerTypeIndividual" },
  { value: 1, name: "customerTypeCompany" }
];

const validationSchema = yup.object({
  customerType: yup.object().shape({
    value: yup.number().required('requiredField'),
    name: yup.string().required('requiredField')
  }),
  firstName: yup.string().required('requiredField'),
  lastName: yup.string().required('requiredField'),
  companyName: yup.string().when("customerType", {
    is: 1,
    then: yup.string().required('requiredField'),
    otherwise: yup.string()
  }),
  email: yup
    .string()
    .email('invalidEmailAddress')
    .required('requiredField'),
  phone: yup
    .string()
    .matches(/^[\s\./0-9]*$/, "invalidPhone")
    .required('requiredField'),
  addressLine1: yup.string().required('requiredField'),
  addressLine2: yup.string(),
  addressLine3: yup.string(),
  addressCity: yup.string().required('requiredField'),
  addressState: yup.string().required('requiredField'),
  addressZipCode: yup.string().required('requiredField'),
  addressCountryIso: yup
    .object()
    .shape({
      TwoLetterIsoCode: yup.string().required('requiredField')
    })
    .nullable()
    .required('requiredField'),
  vatNumber: yup.string(),
  vatIsValid: yup.boolean()
});

const debounceDelayMs = 2000;

const Form = props => {
  const classes = useStyles();
  const {
    handleSubmit,
    handleCancel,
    isSubmitting,
    countries,
    estimatedCountryIsoCode,
    access_token,
    submitError,
    ...other
  } = props;
  const values = props.values;
  // const setFieldValue = props.setFieldValue;
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [vatValidation, setVatValidation] = useState({
    isValid: values.vatIsValid,
    fetching: false
  });
  const {t} = useTranslation();
  const debouncedVatNumber = useDebounce(values.vatNumber, debounceDelayMs);
  const debouncedCompanyName  = useDebounce(values.companyName, debounceDelayMs);
  const debouncedCountry = useDebounce(values.addressCountryIso, debounceDelayMs);

  // INITIALIZE COUNTRY OBJECT
  useEffect(() => {
    if (values.addressCountryIso) {
      log.debug("assigning estimatedCountryIsoCode");
      setSelectedCountry(values.addressCountryIso);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countryChangeHandler = newCountry => {
    log.debug(newCountry);
    setSelectedCountry(newCountry);
  };

  useEffect(() => {
    let isActive = true;
    if (debouncedVatNumber !== "") {
      log.debug(`Should validate value ${debouncedVatNumber}`);
      const url = `${vatBaseUrl}/validateWithCompany`;

      var model = {
        VatNumber: debouncedVatNumber,
        CountryTwoLetterIsoCode: debouncedCountry ? debouncedCountry.TwoLetterIsoCode : "",
        CompanyName: debouncedCompanyName,
      };

      setVatValidation({
        fetching: true
      });

      httpClient.post(url, access_token, model)
        .then(
          data => {
            if(isActive) {
              setVatValidation({
                isValid: data.IsValid,
                fetching: false
              });
            }
          },
          error => {
            log.warn(`Something went wrong: ${error}`);
            setVatValidation({
              fetching: false,
              isValid: false
            });
          });


    }
    return () => {
      isActive = false;
    }
  }, [debouncedVatNumber, access_token, debouncedCompanyName, debouncedCountry]);

  const renderVatValidationAdornment = () => {
    if (vatValidation.fetching) {
      return (
        <Tooltip title={t('vatValidating')}>
          <CircularProgress size={16} />
        </Tooltip>
      );
    } else {
      if (debouncedVatNumber !== "") {
        if (vatValidation.isValid) {
          return (
            <Tooltip title={t('vatValid')} interactive>
              <Done className={classes.iconSuccess} />
            </Tooltip>
          );
        } else {
          return (
            <Tooltip title={t('vatInvalid')} interactive>
              <WarningFull className={classes.iconWarning} />
            </Tooltip>
          );
        }
      }
    }
    return '';
  };

  return (
    <form onSubmit={handleSubmit} className={classes.form}>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={3}>
          <InputSelect
            {...other}
            name="customerType"
            label="customerType"
            options={customerTypeOptions}
            uniqueKeySelector={o => o.value}
            displayNameSelector={o => t(o.name)}
          />
        </Grid>
        <Grid item xs={12} sm={9}>
          <InputText {...other} name="companyName" label={values.customerType.value === 1 ? "companyName" : "idDocumentNumber"} />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <InputText
            {...other}
            inputType="text"
            name="firstName"
            label="firstName"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputText
            {...other}
            inputType="text"
            name="lastName"
            label="lastName"
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <InputText {...other} inputType="email" name="email" label="email" />
        </Grid>
        <Grid item xs={12}>
          <InputText
            {...other}
            inputType="text"
            name="phone"
            label="phoneNumber"
            inputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  className={clsx(
                    classes.denseAdornment,
                    classes.adornmentStart
                  )}
                >
                  {selectedCountry ? selectedCountry.PhoneCode : ""}
                </InputAdornment>
              )
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1}>
        <Grid container item xs={12} sm={6}>
          <Grid item xs={12}>
            <InputText
              {...other}
              inputType="text"
              name="addressLine1"
              label="addressLine1"
            />
          </Grid>
          <Grid item xs={12}>
            <InputText
              {...other}
              inputType="text"
              name="addressLine2"
              label="addressLine2"
            />
          </Grid>
          <Grid item xs={12}>
            <InputText
              {...other}
              inputType="text"
              name="addressLine3"
              label="addressLine3"
            />
          </Grid>
        </Grid>
        <Grid container item xs={12} sm={6}>
          <Grid item xs={12}>
            <InputText
              {...other}
              inputType="text"
              name="addressCity"
              label="city"
            />
          </Grid>
          <Grid item xs={12}>
            <InputText
              {...other}
              inputType="text"
              name="addressState"
              label="province"
            />
          </Grid>
          <Grid item xs={12}>
            <InputText
              {...other}
              inputType="text"
              name="addressZipCode"
              label="zipCode"
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <CountryAutocompleteDS
            {...other}
            name="addressCountryIso"
            label="country"
            countries={countries}
            onSelectedOptionChange={countryChangeHandler}
          />
        </Grid>
        {values.customerType.value === 1 &&
        (values.addressCountryIso && values.addressCountryIso.Vat) ? (
          <Grid item xs={12}>
            <InputText
              {...other}
              inputType="text"
              name="vatNumber"
              label="vatNumber"
              inputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    className={clsx(
                      classes.denseAdornment,
                      classes.adornmentStart
                    )}
                  >
                    {selectedCountry ? selectedCountry.TwoLetterIsoCode : ""}
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment
                    position="end"
                    className={clsx(
                      classes.denseAdornment,
                      classes.adornmentEnd
                    )}
                  >
                    {renderVatValidationAdornment()}
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        ) : null}
      </Grid>

      <div className={classes.formActions}>
        {submitError ? <FormHelperText className={clsx(classes.submitError, "Mui-error")}>{submitError.error.HumanError}</FormHelperText> : null }
        {handleCancel && (<Button type="button" onClick={handleCancel} disabled={isSubmitting}>
          {t('cancel')}
        </Button>)}
        <Button
          type="submit"
          color="primary"
          variant="contained"
          disabled={isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={20} /> : null}{t('submit')}
        </Button>
      </div>
    </form>
  );
};

const BillingInformationForm = props => {
  const {
    current,
    access_token,
    email,
    countries,
    countriesFetching,
    estimatedCountryIsoCode,
    onFetchCountries,
    onCancel,
    onSubmit,
  } = props;
  const formikRef = React.createRef();
  const [ submitError, setSubmitError ] = useState(null);

  let initialValues = {};

  if (!current) {
    const country = countries.find(e => {
      if (
        e.TwoLetterIsoCode.toLowerCase() ===
        estimatedCountryIsoCode.toLowerCase()
      ) {
        return true;
      } else {
        return false;
      }
    });
    initialValues = {
      customerType: customerTypeOptions[0],
      companyName: "",
      firstName: "",
      lastName: "",
      email: email,
      phone: "",
      addressLine1: "",
      addressLine2: "",
      addressLine3: "",
      addressCity: "",
      addressState: "",
      addressZipCode: "",
      addressCountryIso: country,
      vatNumber: "",
      vatIsValid: false
    };
  } else {
    const country = countries.find(e => {
      if (
        e.TwoLetterIsoCode.toLowerCase() ===
        current.AddressCountryIsoCode.toLowerCase()
      ) {
        return true;
      } else {
        return false;
      }
    });

    initialValues = {
      customerType: customerTypeOptions[current.CustomerType],
      companyName: current.CompanyName || "",
      firstName: current.FirstName,
      lastName: current.LastName,
      email: current.Email || "",
      phone: current.Phone || "",
      addressLine1: current.AddressLine1,
      addressLine2: current.AddressLine2 || "",
      addressLine3: current.AddressLine3 || "",
      addressCity: current.AddressCity,
      addressState: current.AddressState,
      addressZipCode: current.AddressZipCode,
      addressCountryIso: country,
      vatNumber: current.Vat.Number || "",
      vatIsValid: current.Vat.IsVerified
    }


  }

  useEffect(() => {
    if (countries && countries.length === 0) {
      onFetchCountries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (values, { setSubmitting }) => {
    if(isEqual(initialValues, values)) {
      onSubmit();
      return;
    }

    const model = {
      CustomerType: values.customerType.value,
      FirstName: values.firstName,
      LastName: values.lastName,
      CompanyName: values.companyName,
      Vat: { Number: values.vatNumber, IsVerified: values.vatIsValid },
      Email: values.email,
      Phone: values.phone,
      AddressLine1: values.addressLine1,
      AddressLine2: values.addressLine2,
      AddressLine3: values.addressLine3,
      AddressCity: values.addressCity,
      AddressState: values.addressState,
      AddressZipCode: values.addressZipCode,
      AddressCountryIsoCode: values.addressCountryIso.TwoLetterIsoCode
    };

    console.log(model);


    const url = `${billingInformationBaseUrl}/createOrUpdate`;

    httpClient.post(url, access_token, model)
      .then(
        ok => {
          setSubmitting(false);
          onSubmit();
        },
        error => {
          console.log(error);
          setSubmitting(false);
          setSubmitError(error);
        }
      );
  };

  const handleCancel = () => {
    // formikRef.current && formikRef.current.resetForm();
    onCancel();
  };

  return (
    <>
      {countriesFetching ? (
        <LogoSpinner />
      ) : (
        <Formik
          ref={formikRef}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          render={props => (
            <Form
              {...props}
              handleCancel={onCancel && handleCancel}
              countries={countries}
              estimatedCountryIsoCode={estimatedCountryIsoCode}
              access_token={access_token}
              submitError={submitError}
            />
          )}
        />
      )}
    </>
  );
};

BillingInformationForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
}

const mapStateToProps = state => ({
  access_token: state.oidc.user.access_token,
  email:
    state.accountVerification.verificationStatus.emailVerificationStatus.Email,
  estimatedCountryIsoCode: state.geo.estimatedCountryIsoCode,
  countries: state.geo.countries,
  countriesFetching: state.geo.fetching,
});

const mapDispatchToProps = dispatch => ({
  onFetchCountries: () => dispatch(geoActions.fetchCountries())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(BillingInformationForm);

